import React, { lazy, Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ScrollToTop } from "./design-system";
import { SiteHeader } from "./components/SiteHeader";
import { SiteFooter } from "./components/SiteFooter";
import routes from "./router/config";
import { GlobalStyles } from "./styles/GlobalStyles";
import "./styles/tokens.css";

const Main = styled.main`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
`;

/* A route change scrolls to the top, except when the URL carries a hash — then
   the anchor wins, which is what /about#contact relies on. */
const ScrollOnRouteChange = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 160 });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

/* The design-system components render plain anchors, by design — they know
   nothing about a router. One document-level listener turns every internal href
   into a route change, which keeps the components free of app concerns and means
   a new link needs no wiring at all. */
const RouteInternalLinks = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  const onClick = (e: React.MouseEvent) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    const anchor = (e.target as HTMLElement).closest?.("a[href]") as HTMLAnchorElement | null;
    if (!anchor || anchor.target === "_blank") return;
    const href = anchor.getAttribute("href") || "";
    if (!href.startsWith("/")) return;
    e.preventDefault();
    navigate(href);
  };

  return (
    <div onClick={onClick} style={{ display: "contents" }}>
      {children}
    </div>
  );
};

const App = () => (
  <BrowserRouter>
    <GlobalStyles />
    <ScrollOnRouteChange />
    <RouteInternalLinks>
      <SiteHeader />
      <Main>
        <Suspense fallback={null}>
          <Routes>
            {routes.map((r) => (
              <Route
                key={r.path}
                path={r.path}
                element={
                  r.redirect ? (
                    <Navigate to={r.redirect} replace />
                  ) : (
                    React.createElement(lazy(() => import(`./pages/${r.component}`)))
                  )
                }
              />
            ))}
          </Routes>
        </Suspense>
      </Main>
      <SiteFooter />
    </RouteInternalLinks>
    <ScrollToTop iconSrc="/img/icons/scroll-top.svg" />
  </BrowserRouter>
);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(<App />);
